import * as React from "react"

import {
  isExemptExternalHost,
  isHttpUrl,
} from "../../utils/externalLinks"
import { Button } from "../Button"
import { ModalShell } from "../ModalShell"

type LeavingSiteInterstitialProviderProps = {
  children: React.ReactNode
}

type InterstitialModalProps = {
  initialFocusRef: React.MutableRefObject<HTMLElement | null>
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
}

const closeButtonClasses =
  "right-[11px] top-[11px] cursor-pointer bg-accent-500 lg:right-[12px] lg:top-[12px]"

const interstitialCopy = {
  heading: "You are leaving this site",
  body: "You are now leaving www.afaxyspharma.com/patients. Links to sites outside of Afaxys are provided as a resource to the viewer. Afaxys, Inc. and its subsidiaries accept no responsibility for the content of linked sites.",
}

function openExternalUrl(href: string) {
  window.open(href, "_blank", "noopener,noreferrer")
}

function InterstitialModal({
  initialFocusRef,
  isOpen,
  onClose,
  onConfirm,
}: InterstitialModalProps) {
  const titleId = React.useId()
  const descriptionId = React.useId()

  return (
    <ModalShell
      ariaDescribedby={descriptionId}
      ariaLabelledby={titleId}
      className="w-[340px] rounded-[15px] border-[12px] border-accent-500 bg-white lg:w-[660px] lg:border-[20px]"
      closeButtonClassName={closeButtonClasses}
      initialFocusRef={initialFocusRef}
      isOpen={isOpen}
      onClose={onClose}
    >
      <h2 className="sr-only" id={titleId}>
        {interstitialCopy.heading}
      </h2>
      <p className="sr-only" id={descriptionId}>
        {interstitialCopy.body}
      </p>

      <div className="px-5 pb-[30px] pt-[10px] lg:px-[48px] lg:pb-[60px] lg:pt-[30px]">
        <div className="flex justify-end" aria-hidden="true">
          <div className="h-[25px] w-[25px] lg:h-[35px] lg:w-[35px]" />
        </div>

        <div className="mt-1 flex flex-col items-center gap-[20px] text-center text-ink-950 lg:mt-[-7px] lg:gap-[25px]">
          <div className="flex flex-col items-center gap-[15px] lg:gap-[25px]">
            <h2 className="w-[298px] font-display text-[20px] font-bold leading-[1.5] tracking-[-0.22px] lg:w-[545px] lg:text-[27px] lg:leading-[40px] lg:tracking-normal">
              {interstitialCopy.heading}
            </h2>

            <p className="w-[300px] font-sans text-[14.5px] leading-5 text-ink-950 lg:w-[560px] lg:text-[16px] lg:leading-[1.35] lg:tracking-[-0.176px]">
              {interstitialCopy.body}
            </p>
          </div>

          <div className="flex flex-row items-center gap-4 lg:gap-5">
            <Button
              className="!h-10 !w-[130px] !cursor-pointer !bg-accent-500 !px-8 lg:!h-[50px] lg:!w-[130px]"
              icon="external"
              onClick={onConfirm}
              ref={node => {
                initialFocusRef.current = node as HTMLElement | null
              }}
              type="button"
              variant="pill"
            >
              OK
            </Button>

            <Button
              className="!h-10 !w-[130px] !cursor-pointer lg:!h-[50px] lg:!w-[130px]"
              onClick={onClose}
              type="button"
              variant="pill"
            >
              Cancel
            </Button>
          </div>
        </div>
      </div>
    </ModalShell>
  )
}

export const LeavingSiteInterstitialProvider: React.FC<
  LeavingSiteInterstitialProviderProps
> = ({ children }) => {
  const confirmButtonRef = React.useRef<HTMLElement | null>(null)
  const [pendingHref, setPendingHref] = React.useState<string | null>(null)

  React.useEffect(() => {
    if (typeof document === "undefined" || typeof window === "undefined") {
      return undefined
    }

    function handleClick(event: MouseEvent) {
      if (
        event.defaultPrevented ||
        event.button !== 0 ||
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey
      ) {
        return
      }

      const clickTarget = event.target

      if (!(clickTarget instanceof Element)) {
        return
      }

      const anchor = clickTarget.closest("a[href]")

      if (!(anchor instanceof HTMLAnchorElement)) {
        return
      }

      if (
        anchor.dataset.leavingSiteInterstitial === "skip" ||
        anchor.hasAttribute("download")
      ) {
        return
      }

      const url = new URL(anchor.href, window.location.href)

      if (!isHttpUrl(url) || url.origin === window.location.origin) {
        return
      }

      if (isExemptExternalHost(url.hostname)) {
        return
      }

      event.preventDefault()
      event.stopPropagation()
      setPendingHref(url.href)
    }

    document.addEventListener("click", handleClick, true)

    return () => {
      document.removeEventListener("click", handleClick, true)
    }
  }, [])

  function handleClose() {
    setPendingHref(null)
  }

  function handleConfirm() {
    if (!pendingHref) {
      return
    }

    const href = pendingHref
    setPendingHref(null)
    openExternalUrl(href)
  }

  return (
    <>
      {children}
      <InterstitialModal
        initialFocusRef={confirmButtonRef}
        isOpen={pendingHref !== null}
        onClose={handleClose}
        onConfirm={handleConfirm}
      />
    </>
  )
}
